import React, { useEffect, useMemo } from 'react';
import { Alert, InputNumber } from 'antd';
import { Button, Card } from '../../design-system';
import { XAxisVariable } from '../../@types/projects/types';
import { useAutoCorrelation } from '../../providers/AutoCorrelation';
import { useCorrelation } from '../../providers/Correlation';
import { FAMILY_KEYS, FAMILY_LABELS } from './autoCorrelationUtils';
import styles from './AutoCorrelation.module.css';
import { getKrSourceAvailability } from './krSourceAvailability';

const X_VARIABLE_OPTIONS: { value: XAxisVariable; label: string }[] = [
  { value: 'fzi', label: 'FZI' },
  { value: 'rqi', label: 'RQI' }
];

/**
 * Step 2 — pick the x-axis variable and gradient difference, then run.
 * The Run action itself lives in the panel's footer action bar.
 */
const RunStep = () => {
  const {
    xVariable,
    setXVariable,
    gradDiff,
    setGradDiff,
    endpointSource,
    setEndpointSource,
    scopeSampleUids
  } = useAutoCorrelation();
  const { projectSamples } = useCorrelation();

  const availability = useMemo(
    () => ({
      lab: getKrSourceAvailability(projectSamples, scopeSampleUids, 'lab'),
      refinement: getKrSourceAvailability(
        projectSamples,
        scopeSampleUids,
        'refinement'
      )
    }),
    [projectSamples, scopeSampleUids]
  );
  const refinementUnavailable = (['water_oil', 'gas_oil'] as const).some(
    family =>
      availability.refinement[family].requested_count > 0 &&
      availability.refinement[family].unavailable_samples.length > 0
  );
  const selectedAvailability = availability[endpointSource];
  const missingSamples = (['water_oil', 'gas_oil'] as const).flatMap(family =>
    selectedAvailability[family].unavailable_samples.map(sample => ({
      ...sample,
      family
    }))
  );
  const refinementMissingSamples = (['water_oil', 'gas_oil'] as const).flatMap(
    family =>
      availability.refinement[family].unavailable_samples.map(sample => ({
        ...sample,
        family
      }))
  );

  useEffect(() => {
    if (endpointSource === 'refinement' && refinementUnavailable) {
      setEndpointSource('lab');
    }
  }, [endpointSource, refinementUnavailable, setEndpointSource]);

  return (
    <div>
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Run configuration</h3>
        <p className={styles.sectionSubtitle}>
          The automatic correlation fits every tendency type for each
          parameter and recommends the most physically consistent combination.
          Nothing is saved to the project until you apply it.
        </p>
      </div>

      <div className={styles.runGrid}>
        <Card variant="outlined" padding="md" fullWidth>
          <Card.Header title="Configuration" />
          <Card.Body>
            <div className={styles.runField}>
              <span className={styles.runFieldLabel}>X-axis variable</span>
              <div
                className={styles.modeGroup}
                role="group"
                aria-label="X-axis variable"
                data-testid="auto-x-variable"
              >
                {X_VARIABLE_OPTIONS.map(option => (
                  <Button
                    key={option.value}
                    variant={
                      xVariable === option.value ? 'primary' : 'secondary'
                    }
                    size="sm"
                    aria-pressed={xVariable === option.value}
                    onClick={() => setXVariable(option.value)}
                  >
                    {option.label}
                  </Button>
                ))}
              </div>
            </div>

            <div className={styles.runField}>
              <span className={styles.runFieldLabel}>Kr value source</span>
              <div
                className={styles.modeGroup}
                role="group"
                aria-label="Kr value source"
                data-testid="auto-endpoint-source"
              >
                <Button
                  variant={endpointSource === 'lab' ? 'primary' : 'secondary'}
                  size="sm"
                  aria-pressed={endpointSource === 'lab'}
                  onClick={() => setEndpointSource('lab')}
                >
                  Lab values
                </Button>
                <Button
                  variant={
                    endpointSource === 'refinement' ? 'primary' : 'secondary'
                  }
                  size="sm"
                  aria-pressed={endpointSource === 'refinement'}
                  disabled={refinementUnavailable}
                  title={
                    refinementUnavailable
                      ? 'One or more selected Kr samples have incomplete calculated refinement values.'
                      : 'Calculated refinement values are available for all selected Kr samples.'
                  }
                  onClick={() => setEndpointSource('refinement')}
                >
                  Refinement values ·{' '}
                  {refinementUnavailable ? 'unavailable' : 'available'}
                </Button>
              </div>
              <span className={styles.runFieldHint}>
                One source is shared by Liao and Brooks-Corey. Refinement never
                falls back to lab fields.
              </span>
            </div>

            <div className={styles.runField}>
              <span className={styles.runFieldLabel}>Gradient difference</span>
              <InputNumber
                value={gradDiff}
                min={0}
                step={0.005}
                onChange={value => {
                  if (typeof value === 'number' && Number.isFinite(value)) {
                    setGradDiff(value);
                  }
                }}
                style={{ width: '10rem' }}
                data-testid="auto-grad-diff"
              />
              <span className={styles.runFieldHint}>
                Used to derive height curves from capillary pressure.
              </span>
            </div>
          </Card.Body>
        </Card>

        <Card variant="outlined" padding="md" fullWidth>
          <Card.Header title="Scope summary" />
          <Card.Body>
            {FAMILY_KEYS.map(family => (
              <div key={family} className={styles.summaryRow}>
                <span>{FAMILY_LABELS[family]}</span>
                <span className={styles.familyCount}>
                  {scopeSampleUids[family].length === 0
                    ? 'skipped'
                    : `${scopeSampleUids[family].length} sample${
                        scopeSampleUids[family].length === 1 ? '' : 's'
                      }`}
                </span>
              </div>
            ))}
            {(['water_oil', 'gas_oil'] as const).map(family => {
              const status = selectedAvailability[family];
              return (
                <div key={`${family}-availability`} className={styles.summaryRow}>
                  <span>{FAMILY_LABELS[family]} {endpointSource}</span>
                  <span className={styles.familyCount}>
                    {status.usable_count}/{status.requested_count} complete
                  </span>
                </div>
              );
            })}
          </Card.Body>
        </Card>
      </div>

      {missingSamples.length > 0 ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: '12px', maxWidth: '900px' }}
          message={`${endpointSource === 'refinement' ? 'Calculated refinement' : 'Lab'} values are incomplete`}
          description={
            <details>
              <summary>
                Show {missingSamples.length} unavailable sample
                {missingSamples.length === 1 ? '' : 's'} and missing fields
              </summary>
              <ul style={{ marginBottom: 0 }}>
                {missingSamples.map(sample => (
                  <li key={`${sample.family}-${sample.sample_uid}`}>
                    {FAMILY_LABELS[sample.family]} — {sample.sample_code}: {' '}
                    {sample.missing_fields.join(', ')}
                  </li>
                ))}
              </ul>
            </details>
          }
        />
      ) : null}
      {endpointSource === 'lab' && refinementMissingSamples.length > 0 ? (
        <Alert
          type="info"
          showIcon
          style={{ marginTop: '12px', maxWidth: '900px' }}
          message="Refinement values are not available for this selection"
          description={
            <details>
              <summary>
                Show missing calculated fields for{' '}
                {refinementMissingSamples.length} sample
                {refinementMissingSamples.length === 1 ? '' : 's'}
              </summary>
              <ul style={{ marginBottom: 0 }}>
                {refinementMissingSamples.map(sample => (
                  <li key={`refinement-${sample.family}-${sample.sample_uid}`}>
                    {FAMILY_LABELS[sample.family]} — {sample.sample_code}: {' '}
                    {sample.missing_fields.join(', ')}
                  </li>
                ))}
              </ul>
            </details>
          }
        />
      ) : null}
    </div>
  );
};

export default RunStep;
